/**
 * Engrave files for many moulds at once - the whole catalog, the moulds on
 * screen, or the ones ticked.
 *
 * Every mould gets a file, with every label on it. A detail still blank in the
 * catalog is engraved blank and counted, not treated as a failure; only a plate that could not be drawn at
 * all fails, and it does not stop the rest. The result says how many of each,
 * and why, before anything is downloaded.
 */
import { useState } from 'react';
import { CircleAlert, CircleCheck, Download, Loader2, PenTool } from 'lucide-react';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Checkbox, Select } from '../../components/ui/field.js';
import {
  ApiClientError,
  downloadEngraveFiles,
  generateEngraveFiles,
  isNetworkError,
  type EngraveBatchResult,
} from '../../api/client.js';
import { notifyEngraveDownloaded, useEngraveDownloads, useOfferedDesigns } from './useEngrave.js';

type Scope = 'all' | 'visible' | 'selected';

export function EngraveBatchDialog({
  open,
  onOpenChange,
  allCount,
  allIds,
  visibleIds,
  selectedIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allCount: number;
  /** Every mould in the catalog, for leaving out the ones already downloaded. */
  allIds: string[];
  /** The moulds the list shows now, when it is filtered; null when it shows them all. */
  visibleIds: string[] | null;
  selectedIds: string[];
}) {
  const [scope, setScope] = useState<Scope>(selectedIds.length ? 'selected' : 'all');
  const [design, setDesign] = useState<string>('');
  const [busy, setBusy] = useState<'generating' | 'downloading' | null>(null);
  const [result, setResult] = useState<EngraveBatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Plates already cut need not be drawn again - but may be, so it is a choice.
  const [skipDownloaded, setSkipDownloaded] = useState(false);
  const designs = useOfferedDesigns();
  const downloads = useEngraveDownloads();

  const scopeIds = scope === 'selected' ? selectedIds : scope === 'visible' ? (visibleIds ?? []) : allIds;
  const alreadyDownloaded = scopeIds.filter((id) => downloads[id]).length;
  const count = skipDownloaded
    ? scopeIds.length - alreadyDownloaded
    : scope === 'selected'
      ? selectedIds.length
      : scope === 'visible'
        ? (visibleIds?.length ?? 0)
        : allCount;

  function explain(e: unknown, fallback: string): string {
    if (isNetworkError(e)) return 'No connection. Engrave files are drawn on the server.';
    if (e instanceof ApiClientError) return e.message;
    if (e instanceof DOMException && e.name === 'TimeoutError') return 'The server took too long. Try fewer moulds at a time.';
    return fallback;
  }

  async function generate() {
    setBusy('generating');
    setError(null);
    setResult(null);
    try {
      const mouldIds = skipDownloaded
        ? scopeIds.filter((id) => !downloads[id])
        : scope === 'selected'
          ? selectedIds
          : scope === 'visible'
            ? (visibleIds ?? undefined)
            : undefined;
      setResult(await generateEngraveFiles({ mouldIds, design: design || null }));
    } catch (e) {
      setError(explain(e, 'The engrave files could not be generated.'));
    } finally {
      setBusy(null);
    }
  }

  async function download() {
    if (!result) return;
    setBusy('downloading');
    setError(null);
    try {
      const blob = await downloadEngraveFiles(result.jobId);
      // Object URL, then click, then revoke on a timer: revoking straight away
      // cancels the download in some browsers.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      // The server has recorded every mould in the ZIP: show their ticks now.
      notifyEngraveDownloaded();
    } catch (e) {
      setError(explain(e, 'The ZIP could not be downloaded.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title="Generate engrave files"
      description="One DXF file per mould, named by its mould code, in one ZIP. Every plate carries every label; a detail still blank in the catalog is engraved blank - it never stops the plate being made."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={!!busy}>
            Close
          </Button>
          {result && result.generated > 0 ? (
            <Button onClick={() => void download()} loading={busy === 'downloading'} disabled={!!busy}>
              <Download className="size-4" /> Download ZIP
            </Button>
          ) : (
            <Button onClick={() => void generate()} loading={busy === 'generating'} disabled={!!busy || count === 0}>
              <PenTool className="size-4" /> Generate {count} file{count === 1 ? '' : 's'}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        <fieldset className="grid gap-2" disabled={!!busy}>
          <legend className="mb-1 text-sm font-medium">Which moulds</legend>
          <ScopeOption value="all" scope={scope} onChange={setScope} label={`All ${allCount} moulds in the catalog`} />
          {visibleIds ? (
            <ScopeOption
              value="visible"
              scope={scope}
              onChange={setScope}
              label={`The ${visibleIds.length} moulds the list shows now`}
            />
          ) : null}
          {selectedIds.length ? (
            <ScopeOption
              value="selected"
              scope={scope}
              onChange={setScope}
              label={`The ${selectedIds.length} selected mould${selectedIds.length === 1 ? '' : 's'}`}
            />
          ) : null}
        </fieldset>

        {alreadyDownloaded > 0 ? (
          <Checkbox
            label="Leave out moulds already downloaded"
            description={`${alreadyDownloaded} of these ${scopeIds.length} already have their engrave file downloaded (ticked in the catalog). Leave unticked to download them again.`}
            checked={skipDownloaded}
            onChange={(e) => setSkipDownloaded(e.target.checked)}
            disabled={!!busy}
          />
        ) : null}

        {/* Only the designs on offer: Design B alone unless the server offers more. */}
        {designs.length > 1 ? (
          <label className="grid gap-1.5 text-sm">
            <span className="font-medium">Plate design</span>
            <Select
              value={design}
              onChange={(e) => setDesign(e.target.value)}
              disabled={!!busy}
              options={[
                { value: '', label: "Each mould's own design", hint: `${designs[0]!.name} unless another was chosen` },
                ...designs.map((d) => ({ value: d.code, label: `${d.name} for every mould`, hint: d.summary })),
              ]}
            />
          </label>
        ) : (
          <p className="text-sm">
            <span className="font-medium">Plate design:</span> {designs[0]?.name ?? 'Design B'}
          </p>
        )}

        {busy === 'generating' ? (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Drawing {count} plate{count === 1 ? '' : 's'} - a large
            catalog takes a minute or two…
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        {result ? (
          <div role="status" className="grid gap-3 rounded-xl border border-border p-3 text-sm">
            <dl className="grid grid-cols-3 gap-2 text-center">
              <Count label="Total" value={result.total} />
              <Count label="Generated" value={result.generated} tone="good" />
              <Count label="Failed" value={result.failed} tone={result.failed ? 'bad' : undefined} />
            </dl>
            <p className="flex items-start gap-2 text-muted-foreground">
              <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
              <span>
                {result.fileName} · {formatBytes(result.bytes)} · {result.design}.
                {result.withBlankDetails
                  ? ` ${result.withBlankDetails} plate${result.withBlankDetails === 1 ? ' has' : 's have'} a detail engraved blank - listed in _SUMMARY.txt inside the ZIP.`
                  : ''}
              </span>
            </p>
            {result.failures.length ? (
              <div className="rounded-lg bg-danger-subtle px-3 py-2 text-danger">
                <p className="mb-1 flex items-center gap-1.5 font-medium">
                  <CircleAlert className="size-4" aria-hidden /> No file for these:
                </p>
                <ul className="list-disc pl-5">
                  {result.failures.slice(0, 50).map((f) => (
                    <li key={f.mouldId}>
                      <span className="font-mono">{f.code}</span>: {f.reason}
                    </li>
                  ))}
                </ul>
                {result.failures.length > 50 ? <p className="mt-1">…and {result.failures.length - 50} more.</p> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function ScopeOption({
  value,
  scope,
  onChange,
  label,
}: {
  value: Scope;
  scope: Scope;
  onChange: (s: Scope) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="radio" name="engrave-scope" checked={scope === value} onChange={() => onChange(value)} />
      {label}
    </label>
  );
}

function Count({ label, value, tone }: { label: string; value: number; tone?: 'good' | 'bad' }) {
  return (
    <div className="rounded-lg bg-surface-sunken px-2 py-1.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-lg font-semibold ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
